/**
 * Dark Mode Support
 */
import React from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const THEME_KEY_BASE = 'agenturcrm_theme';
const LAST_USER_KEY = 'agenturcrm_last_user';

const getThemeStorageKey = () => {
  try {
    const lastUser = localStorage.getItem(LAST_USER_KEY);
    return lastUser ? `${THEME_KEY_BASE}_${lastUser}` : THEME_KEY_BASE;
  } catch {
    return THEME_KEY_BASE;
  }
};

const loadSavedTheme = (): Theme | null => {
  try {
    const key = getThemeStorageKey();
    const saved = localStorage.getItem(key) as Theme | null;
    if (saved) return saved;
    const legacy = localStorage.getItem(THEME_KEY_BASE) as Theme | null;
    return legacy || null;
  } catch {
    return null;
  }
};

export const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

/**
 * Theme Provider Component
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialTheme = React.useMemo<Theme>(() => loadSavedTheme() || 'light', []);

  const [theme, setThemeState] = React.useState<Theme>(initialTheme);

  const [isDark, setIsDark] = React.useState(() => {
    if (initialTheme === 'dark') return true;
    if (initialTheme === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        setIsDark(mediaQuery.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  React.useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  React.useEffect(() => {
    const refreshTheme = () => {
      const next = loadSavedTheme() || 'light';
      setThemeState(next);
      if (next === 'dark') {
        setIsDark(true);
      } else if (next === 'light') {
        setIsDark(false);
      } else {
        setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
    };

    window.addEventListener('agenturcrm-theme-refresh', refreshTheme as any);
    return () => window.removeEventListener('agenturcrm-theme-refresh', refreshTheme as any);
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(getThemeStorageKey(), newTheme);
    } catch {}

    if (newTheme === 'dark') {
      setIsDark(true);
    } else if (newTheme === 'light') {
      setIsDark(false);
    } else {
      setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  };

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, isDark, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme
 */
export function useTheme(): ThemeContextType {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

/**
 * Theme Toggle Button
 */
export const ThemeToggle: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${className}`}
      aria-label={isDark ? 'Light mode' : 'Dark mode'}
    >
      {isDark ? '☀️' : '🌙'}
    </button>
  );
};

/**
 * Utility to get theme-aware colors
 */
export function getThemeColor(lightColor: string, darkColor: string, isDark?: boolean): string {
  if (isDark === undefined) {
    isDark = document.documentElement.classList.contains('dark');
  }
  return isDark ? darkColor : lightColor;
}
